import { Icon } from './Icon';
import { Component, register } from '../Component';
import { theme } from '../theme';
import { glassSurface } from './glassStyle';
import { underlineField } from './fieldStyle';

type OptionType = { value: string; label: string };

interface Props {
  value?: string;
  options: OptionType[];
  onChange?: (value: string) => void;
  disabled?: boolean;
}

@register('x-select')
export class Select extends Component<Props> {
  private applyValue?: (value: string | undefined) => void;
  private applyDisabled?: (disabled: boolean) => void;

  get value(): string | undefined {
    return this._props?.value;
  }

  /** For panels that build their DOM once and mutate it. Before init() there
   *  is nothing to paint, but props feed the first render. */
  set value(value: string | undefined) {
    if (!this._props || this._props.value === value) return;
    this._props.value = value;
    this.applyValue?.(value);
  }

  get disabled(): boolean {
    return this._props?.disabled === true;
  }

  set disabled(value: boolean) {
    if (!this._props || (this._props.disabled === true) === value) return;
    this._props.disabled = value;
    this.applyDisabled?.(value);
  }

  init() {
    const [showDropDown, setShowDropDown] = this.useState(false);

    // Positioned at show time rather than only during render: toggling the
    // dropdown deliberately skips a re-render, so a rect measured in the render
    // pass would be one interaction stale (and zero-sized on the first).
    const positionOptions = () => {
      const rect = this.getBoundingClientRect();
      options.style.top = `${rect.bottom}px`;
      options.style.left = `${rect.left}px`;
      options.style.width = `${rect.width}px`;
    };

    // `parentElement` doubles as "already shown". The list is only ever taken
    // out of the top layer by being removed from the DOM, so connectedness and
    // popover state cannot disagree — which saves probing ':popover-open', a
    // selector older engines throw on.
    //
    // Append before showing: showPopover() requires a connected element.
    const showOptions = () => {
      const alreadyShown = !!options.parentElement;
      if (!alreadyShown) document.body.appendChild(options);
      positionOptions();
      // Drives the open styling (accent border, flipped arrow). Set on the host
      // directly because toggling the dropdown deliberately skips a re-render,
      // so it cannot come from a class in the render function.
      this.classList.add('open');

      if (!alreadyShown && usePopover) {
        try {
          options.showPopover();
        } catch {
          /* Refused for reasons we cannot recover from anyway. The list is in
             the DOM and styled, so it stays usable outside the top layer. */
        }
      }
    };

    const hideOptions = () => {
      this.classList.remove('open');
      if (options.parentElement) document.body.removeChild(options);
    };

    const handleShowOptions = () => {
      if (this.props.disabled) return;
      setShowDropDown(!showDropDown(), false);

      if (showDropDown()) {
        showOptions();

        setTimeout(
          () => document.body.addEventListener('click', handleElsewhereClicked),
          30
        );
      } else {
        hideOptions();
        cleanup();
      }
    };

    // Create event cleanup function
    const cleanup = () => {
      document.body.removeEventListener('click', handleElsewhereClicked);
    };

    const handleElsewhereClicked = (e: MouseEvent) => {
      cleanup();
      if ((e.target as HTMLElement).classList.contains('option')) return;
      setShowDropDown(false);
    };

    const handleOptionClick = (option: OptionType) => {
      this.props.onChange?.(option.value);
      cleanup();
      setShowDropDown(false);
    };

    let options = (
      <Options>
        {this.props.options.map((option) => (
          <Option
            selected={this.props.value === option.value}
            option={option}
            onclick={() => handleOptionClick(option)}
          />
        ))}
      </Options>
    );

    // Appending the list to document.body escapes any ancestor overflow or
    // stacking context, which is not enough on its own: a Modal is promoted to
    // the browser's top layer (see Popup), and nothing in the normal layer can
    // paint above — or be clicked through — a top-layer element. So the list
    // joins the top layer too. 'manual' rather than 'auto' because an auto
    // popover closes every other open popover that is not its DOM ancestor,
    // which would dismiss the very modal the select sits in.
    const usePopover = typeof options.showPopover === 'function';
    if (usePopover) options.setAttribute('popover', 'manual');

    this.onCleanup = () => {
      hideOptions();
      cleanup();
    };

    // Built once. render() and the public setters all paint onto these rather
    // than returning a fresh tree, so a value or disabled change never swaps
    // the trigger out from under the pointer.
    const valueLabel = (<div class="value" />) as HTMLDivElement;
    const trigger = (
      <div class="select" onmouseup={handleShowOptions}>
        {valueLabel}
        <Icon icon="chevron-down" size="s" />
      </div>
    ) as HTMLDivElement;

    this.applyValue = (value: string | undefined) => {
      valueLabel.textContent =
        this.props.options.find((o) => o.value === value)?.label ?? '';
    };

    this.applyDisabled = (disabled: boolean) => {
      trigger.classList.toggle('disabled', disabled);
      trigger.setAttribute('aria-disabled', disabled.toString());
      // Disabling while open closes the list: a dropdown the control no longer
      // answers to must not be left hanging over the page.
      if (disabled && showDropDown()) {
        setShowDropDown(false, false);
        cleanup();
        hideOptions();
      }
    };

    return () => {
      this.applyValue!(this.props.value);
      this.applyDisabled!(this.props.disabled === true);
      if (!showDropDown()) hideOptions();
      else positionOptions();
      return trigger;
    };
  }

  getStyle() {
    return StyledSelect;
  }
}

@register('x-option')
export class Option extends Component<{
  option: OptionType;
  selected?: boolean;
  onclick?: () => void;
}> {
  init() {
    return () => (
      this.classList.toggle('selected', this.props.selected),
      (
        <div class="option" onclick={this.props.onclick}>
          {this.props.option.label}
        </div>
      )
    );
  }

  getStyle() {
    return StyledOption;
  }
}

@register('x-options')
export class Options extends Component {
  init() {
    this.className = 'options';
    return () => <slot />;
  }

  getStyle() {
    return StyledOptions;
  }
}

const StyledOption = cssStylesheet(css`
  :host {
    display: block;
    font-weight: 400;
    font-size: ${theme.colors.fontSizeMedium};
    background-color: transparent;
    color: ${theme.colors.onField};
    transition: background-color 0.15s;
  }

  /* The click target, so it fills the whole row the hover lights up. Padding
     and size track the field's (underlineField) so each label lines up with
     the value above it. */
  .option {
    display: flex;
    align-items: center;
    box-sizing: border-box;
    width: 100%;
    min-height: ${theme.sizes.control};
    padding: 0 ${theme.space.xs};
  }

  :host(:hover) {
    background-color: ${theme.colors.subtle400};
  }

  :host(.selected) {
    background-color: ${theme.colors.primary400};
    color: ${theme.colors.onPrimary400};
  }

  :host(.selected:hover) {
    background-color: ${theme.colors.primary500};
    color: ${theme.colors.onPrimary500};
  }
`);

const StyledOptions = cssStylesheet(css`
  :host {
    /* Fixed, not absolute: top/left are set from getBoundingClientRect, which
       is viewport-relative — as absolute they would be read against the
       document and the list would slide away from its select on scroll. */
    position: fixed;
    display: block;
    /* Matches the control it drops out of. */
    border-radius: 4px;
    cursor: pointer;
    /* Mostly opaque: a dropdown opens over other text. */
    ${glassSurface('85%')}
    border: 1px solid ${theme.colors.glassEdge};
    box-shadow: ${theme.colors.shadowShort2};
    /* A long list scrolls rather than running off the viewport. x stays hidden
       so the first and last options are clipped to the radius. */
    max-height: 16rem;
    overflow-x: hidden;
    overflow-y: auto;
    /* Only relevant on the non-popover fallback path; in the top layer there is
       nothing left to stack against. */
    z-index: 10;
  }

  /* Top-layer popover: the UA centres popovers with inset:0 + margin:auto and
     adds its own box styling, all of which would override the position we set
     inline. Strip it back so this stays an anchored dropdown — but leave
     overflow alone, or the scrolling set above is undone. */
  :host([popover]) {
    inset: auto;
    margin: 0;
    padding: 0;
  }
`);

const StyledSelect = cssStylesheet(css`
  :host {
    position: relative;
    display: block;
  }

  .select {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    /* The whole row is a button, so it must not look like selectable text —
       a caret over the arrow reads as "this does nothing". */
    cursor: pointer;
    user-select: none;
  }

  /* An open dropdown is the equivalent of a focused field. */
  ${underlineField('.select', ':host(.open) .select', '.select.disabled')}

  .value {
    font-weight: 400;
    /* A label longer than the control must not push the arrow off the row. */
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  x-icon {
    flex-shrink: 0;
    transition: transform 0.25s;
  }

  /* Points at the list while it is open. */
  :host(.open) x-icon {
    transform: rotate(180deg);
  }
`);
